import { useState, useEffect } from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLandscape: boolean;
  isMobileLandscape: boolean; // Mobile device in landscape orientation
  width: number;
  height: number;
}

export const useDevice = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isLandscape: false,
    isMobileLandscape: false,
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isLandscape = width > height;
      
      // Mobile device: narrower dimension < 768px (so in landscape, height < 768)
      // This catches phones that are ~400px tall when rotated
      const isMobileDevice = Math.min(width, height) < 500;
      
      // Mobile landscape: mobile device + landscape orientation
      const isMobileLandscape = isMobileDevice && isLandscape;
      
      setDeviceInfo({
        // In landscape mobile, we want desktop-like layout, so isMobile should be false
        isMobile: isMobileDevice && !isLandscape,
        isTablet: !isMobileDevice && width >= 768 && width < 1024,
        isDesktop: width >= 1024 || isMobileLandscape, // Treat mobile landscape as desktop-like
        isLandscape,
        isMobileLandscape,
        width: width,
        height: height,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Also listen for orientation change events (more reliable on mobile)
    window.addEventListener('orientationchange', () => {
      // Small delay to let the browser update dimensions after orientation change
      setTimeout(handleResize, 100);
    });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return deviceInfo;
};
